import { UseTellescopeQueryOptions } from "../../types";
import { useTellescopeQuery } from "../../use-tellescope-query";

export interface USDAFoodSummary {
  fdcId: number;
  description: string;
  /** kcal per 100g */
  calories?: number;
  /** g per 100g */
  protein?: number;
  /** g per 100g */
  carbs?: number;
  /** g per 100g */
  fat?: number;
  /** g per 100g */
  fiber?: number;
}

export interface USDAFoodSearchResponse {
  data: {
    foods: USDAFoodSummary[];
    totalHits: number;
  };
}

export interface USDAFoodDetailResponse {
  data: USDAFoodSummary;
}

const MACRO_KEYS = ["calories", "protein", "carbs", "fat", "fiber"] as const;

const isUSDAFoodSummary = (value: unknown): value is USDAFoodSummary => {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  if (typeof record.fdcId !== "number" || typeof record.description !== "string") return false;
  return MACRO_KEYS.every((key) => record[key] === undefined || typeof record[key] === "number");
};

export const isUSDAFoodSearchResponse = (value: unknown): value is USDAFoodSearchResponse => {
  if (typeof value !== "object" || value === null || !("data" in value)) return false;
  const { data } = value as { data: unknown };
  if (typeof data !== "object" || data === null) return false;
  const { foods, totalHits } = data as Record<string, unknown>;
  return Array.isArray(foods) && foods.every(isUSDAFoodSummary) && typeof totalHits === "number";
};

export const isUSDAFoodDetailResponse = (value: unknown): value is USDAFoodDetailResponse => {
  if (typeof value !== "object" || value === null || !("data" in value)) return false;
  return isUSDAFoodSummary((value as { data: unknown }).data);
};

export interface USDAFoodSearchQuery {
  keywords: string;
  pageSize?: number;
}

export interface UseUsdaFoodSearchQueryOptions<TError = Error, TData = USDAFoodSearchResponse>
  extends Omit<UseTellescopeQueryOptions<"integrations", "proxy_read", TError, TData>, "model" | "method" | "request"> {
  /** Search parameters. The query is disabled (no request fires) while this is undefined — pass it only once the user explicitly triggers a search. */
  query: USDAFoodSearchQuery | undefined;
}

/**
 * Searches USDA FoodData Central via the Tellescope integration proxy.
 * Callers control when a request fires by only supplying `query` on explicit user action
 * (e.g. a Search button) — this hook does not debounce or search automatically as the user types.
 */
export const useUsdaFoodSearchQuery = <TError = Error, TData = USDAFoodSearchResponse>({
  query,
  enabled,
  ...options
}: UseUsdaFoodSearchQueryOptions<TError, TData>) =>
  useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: { integration: "USDA", type: "search", query: JSON.stringify(query ?? {}) },
    enabled: !!query && (enabled ?? true),
    ...options,
  });

export interface UseUsdaFoodDetailQueryOptions<TError = Error, TData = USDAFoodDetailResponse>
  extends Omit<UseTellescopeQueryOptions<"integrations", "proxy_read", TError, TData>, "model" | "method" | "request"> {
  /** USDA FDC id. The query is disabled while this is undefined. */
  fdcId: number | undefined;
}

/** Fetches full nutrient detail for a single USDA food by FDC id via the Tellescope integration proxy. */
export const useUsdaFoodDetailQuery = <TError = Error, TData = USDAFoodDetailResponse>({
  fdcId,
  enabled,
  ...options
}: UseUsdaFoodDetailQueryOptions<TError, TData>) =>
  useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: { integration: "USDA", type: "food", id: fdcId !== undefined ? String(fdcId) : "" },
    enabled: fdcId !== undefined && (enabled ?? true),
    ...options,
  });
